import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { schemeAPI } from '../../services/api';
import { openExternalWithNotice } from '../../utils/externalLinks';
import { formatMarathiDate, formatMarathiNumber, toMarathiDigits } from '../../utils/marathi';
import './SchemeDetailPage.css';

const LEVEL_META = {
  central: { label: 'केंद्र योजना', color: '#1d67d2', bg: '#eaf3ff' },
  state: { label: 'राज्य योजना', color: '#8b45d8', bg: '#f3eaff' },
  district: { label: 'जिल्हा योजना', color: '#f97316', bg: '#fff0e5' },
  local: { label: 'ग्रामपंचायत योजना', color: '#1f9d55', bg: '#e9f8ef' },
};

const CATEGORY_META = {
  housing: { label: 'घरकुल', icon: 'home', color: '#2563eb', bg: '#edf4ff' },
  financial: { label: 'आर्थिक', icon: 'coins', color: '#9b59b6', bg: '#f4eafd' },
  health: { label: 'आरोग्य', icon: 'heart', color: '#ef476f', bg: '#fff0f4' },
  education: { label: 'शिक्षण', icon: 'cap', color: '#7c3aed', bg: '#f1eaff' },
  elderly: { label: 'ज्येष्ठ नागरिक', icon: 'user', color: '#0ea5e9', bg: '#e9f7ff' },
  agriculture: { label: 'कृषी', icon: 'leaf', color: '#22a447', bg: '#eaf8ef' },
  women: { label: 'महिला व बालकल्याण', icon: 'woman', color: '#d9467a', bg: '#fff0f6' },
  other: { label: 'इतर', icon: 'grid', color: '#64748b', bg: '#eef2f6' },
};

function Icon({ name, size = 22 }) {
  const paths = {
    home: <><path d="m3 11 9-8 9 8" /><path d="M5 10v11h14V10M9 21v-6h6v6" /></>,
    coins: <><ellipse cx="12" cy="6" rx="7" ry="3" /><path d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" /></>,
    heart: <><path d="M20.8 4.6a5.4 5.4 0 0 0-7.6 0L12 5.8l-1.2-1.2a5.4 5.4 0 1 0-7.6 7.6L12 21l8.8-8.8a5.4 5.4 0 0 0 0-7.6Z" /><path d="M7 12h3l1-3 2 6 1-3h3" /></>,
    cap: <><path d="m3 8 9-5 9 5-9 5-9-5Z" /><path d="M7 10v5c3 2 7 2 10 0v-5" /></>,
    user: <><circle cx="12" cy="7" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
    leaf: <><path d="M5 21c8 0 14-6 14-14V4h-3C8 4 3 9 3 17v4h2Z" /><path d="M5 19c4-5 8-8 14-12" /></>,
    woman: <><circle cx="12" cy="7" r="4" /><path d="M12 11v10M8 15h8M9 21h6" /></>,
    grid: <><rect x="4" y="4" width="6" height="6" rx="1" /><rect x="14" y="4" width="6" height="6" rx="1" /><rect x="4" y="14" width="6" height="6" rx="1" /><rect x="14" y="14" width="6" height="6" rx="1" /></>,
    check: <path d="m5 12 4 4L19 6" />,
    file: <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z" /><path d="M14 3v6h6M8 13h8M8 17h5" /></>,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 11h18" /></>,
    arrow: <path d="M5 12h14M13 5l7 7-7 7" />,
    back: <path d="M19 12H5M11 5l-7 7 7 7" />,
    external: <><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></>,
  };
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name] || paths.grid}</svg>;
}

const levelMeta = level => LEVEL_META[level] || { label: level || 'योजना', color: '#64748b', bg: '#eef2f6' };
const categoryMeta = category => CATEGORY_META[category] || CATEGORY_META.other;
const formatDate = value => value ? formatMarathiDate(value, { day: '2-digit', month: 'long', year: 'numeric' }) : 'सतत सुरू';
const externalUrl = value => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};
const parseJsonValue = value => {
  let current = value;
  for (let i = 0; i < 4; i += 1) {
    if (typeof current !== 'string') return current;
    const trimmed = current.trim();
    if (!trimmed) return '';
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed === current) return current;
      current = parsed;
    } catch {
      return current;
    }
  }
  return current;
};

const cleanDisplayText = value => String(value || '')
  .trim()
  .replace(/^[[\]{}\s'"]+|[[\]{}\s'"]+$/g, '')
  .replace(/^"(.*)"$/, '$1')
  .trim();

const itemText = item => {
  const parsed = parseJsonValue(item);
  if (!parsed) return '';
  if (typeof parsed === 'string') {
    const text = cleanDisplayText(parsed);
    const fieldMatch = text.match(/"(?:question|name|document_name|label|title|text)"\s*:\s*"([^"]+)"/i);
    return toMarathiDigits(fieldMatch?.[1] || text);
  }
  if (Array.isArray(parsed)) return parsed.map(itemText).filter(Boolean).join(', ');
  return toMarathiDigits(
    parsed.question ||
    parsed.name ||
    parsed.document_name ||
    parsed.label ||
    parsed.title ||
    parsed.text ||
    parsed.description ||
    ''
  );
};

const parseStructuredList = value => {
  const parsed = parseJsonValue(value);
  if (!parsed) return [];

  if (Array.isArray(parsed)) {
    const joined = parsed.every(item => typeof item === 'string') ? parsed.join(',') : '';
    const parsedJoined = joined ? parseJsonValue(joined) : null;
    if (Array.isArray(parsedJoined)) return parseStructuredList(parsedJoined);
    return parsed.flatMap(item => parseStructuredList(item));
  }

  if (typeof parsed === 'object') {
    if (parsed.question || parsed.name || parsed.document_name || parsed.label || parsed.title || parsed.text) {
      return [parsed];
    }
    return Object.values(parsed).flatMap(item => parseStructuredList(item));
  }

  const text = String(parsed || '').trim();
  if (!text) return [];
  const parsedAgain = parseJsonValue(text);
  if (parsedAgain !== text) return parseStructuredList(parsedAgain);
  return text.split(/\r?\n/).map(item => item.trim()).filter(Boolean);
};

const normalizeDocumentRequirements = value => {
  const seen = new Set();
  return parseStructuredList(value).map(item => {
    const parsed = parseJsonValue(item);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return {
        name: itemText(parsed),
        is_required: (parsed.is_required ?? parsed.required ?? true) !== false,
      };
    }
    return { name: itemText(parsed), is_required: true };
  }).filter(item => {
    const key = item.name.toLowerCase();
    if (!item.name || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

function DetailSection({ id, icon, title, children }) {
  return (
    <section id={id} className="scheme-detail-card">
      <h2><Icon name={icon} />{title}</h2>
      {children}
    </section>
  );
}

export default function SchemeDetailPage() {
  const { tenantSlug, schemeId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [scheme, setScheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [applyData, setApplyData] = useState({ eligibility: {}, documents: {}, notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [applyMessage, setApplyMessage] = useState(null);

  useEffect(() => {
    setLoading(true);
    setShowApplyForm(false);
    setApplyData({ eligibility: {}, documents: {}, notes: '' });
    setApplyMessage(null);
    schemeAPI.getById(tenantSlug, schemeId)
      .then(res => setScheme(res.data.scheme))
      .catch(() => setError('योजनेची माहिती लोड करता आली नाही.'))
      .finally(() => setLoading(false));
  }, [tenantSlug, schemeId]);

  useEffect(() => {
    if (loading || !scheme || !window.location.hash) return;
    const targetId = window.location.hash.slice(1);
    window.requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [loading, scheme]);

  const docs = useMemo(() => normalizeDocumentRequirements(scheme?.required_documents), [scheme]);
  const eligibility = useMemo(() => parseStructuredList(scheme?.eligibility_criteria), [scheme]);
  const applicationSteps = useMemo(() => parseStructuredList(scheme?.application_process), [scheme]);
  const isCitizen = user?.role === 'citizen';
  const portalUrl = externalUrl(scheme?.portal_url);
  const canApplyOnGp = Boolean(scheme?.tenant_id) && scheme?.scheme_level === 'local' && !portalUrl;
  const returnPath = `/gp/${tenantSlug}/schemes/${schemeId}`;
  const loginTarget = `/login?gp=${tenantSlug}&redirect=${encodeURIComponent(returnPath)}`;
  const applicationChannel = canApplyOnGp ? 'direct' : portalUrl ? 'external' : 'guidance';

  const setEligibilityConfirmation = (index, checked) => {
    setApplyData(prev => ({
      ...prev,
      eligibility: { ...prev.eligibility, [index]: checked },
    }));
  };

  const setDocumentConfirmation = (index, checked) => {
    setApplyData(prev => ({
      ...prev,
      documents: { ...prev.documents, [index]: checked },
    }));
  };

  const submitSchemeApplication = async (event) => {
    event.preventDefault();
    if (!canApplyOnGp || !isCitizen || submitting) return;

    const eligibilityReady = eligibility.every((_, index) => applyData.eligibility[index]);
    const documentsReady = docs.every((doc, index) => doc.is_required === false || applyData.documents[index]);
    if (!eligibilityReady || !documentsReady) {
      setApplyMessage({
        type: 'error',
        text: 'कृपया सर्व पात्रता निकष आणि आवश्यक कागदपत्रांची पुष्टी करा.',
      });
      return;
    }

    setSubmitting(true);
    setApplyMessage(null);
    try {
      const res = await schemeAPI.apply(tenantSlug, {
        scheme_id: scheme.id,
        application_data: {
          submitted_from: 'public_scheme_detail',
          scheme_name: scheme.name,
          eligibility_confirmations: eligibility.map((item, index) => ({
            text: itemText(item),
            confirmed: Boolean(applyData.eligibility[index]),
          })),
          document_confirmations: docs.map((item, index) => ({
            name: item.name,
            text: itemText(item),
            is_required: item.is_required !== false,
            confirmed: Boolean(applyData.documents[index]),
          })),
          applicant_notes: applyData.notes.trim(),
        },
      });
      setShowApplyForm(false);
      setApplyData({ eligibility: {}, documents: {}, notes: '' });
      setApplyMessage({
        type: 'success',
        text: 'योजना अर्ज यशस्वीपणे सबमिट झाला.',
        reference: res.data?.reference || res.data?.application?.application_uid,
      });
    } catch (err) {
      setApplyMessage({
        type: 'error',
        text: err.response?.data?.error || 'योजना अर्ज सबमिट करता आला नाही. कृपया पुन्हा प्रयत्न करा.',
        reference: err.response?.data?.application?.application_uid,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="scheme-detail-state">योजनेची माहिती लोड होत आहे...</div>;
  if (error) return <div className="scheme-detail-state error">{error}</div>;
  if (!scheme) return <div className="scheme-detail-state">योजना उपलब्ध नाही.</div>;

  const category = categoryMeta(scheme.category);
  const level = levelMeta(scheme.scheme_level);

  return (
    <div className="scheme-detail-page" style={{ '--scheme-color': category.color, '--scheme-bg': category.bg, '--level-color': level.color, '--level-bg': level.bg }}>
      <section className="scheme-detail-hero">
        <div>
          <nav><Link to={`/gp/${tenantSlug}`}>मुख्यपृष्ठ</Link><span>›</span><Link to={`/gp/${tenantSlug}/schemes`}>योजना</Link><span>›</span><strong>योजना माहिती</strong></nav>
          <h1>{scheme.name}</h1>
          <p>{scheme.description || 'या योजनेची सविस्तर माहिती ग्रामपंचायत कार्यालयातून उपलब्ध होईल.'}</p>
          <div className="scheme-detail-badges">
            <span>{level.label}</span>
            <span>{category.label}</span>
            {scheme.is_active && <span>सक्रिय</span>}
          </div>
        </div>
        <button type="button" onClick={() => navigate(-1)}><Icon name="back" size={16} /> मागे जा</button>
      </section>

      <section className="scheme-detail-strip">
        <article><Icon name="coins" /><p><span>लाभ</span><strong>{toMarathiDigits(scheme.benefit_amount || scheme.benefit_details || 'माहिती उपलब्ध नाही')}</strong></p></article>
        <article><Icon name="grid" /><p><span>स्तर</span><strong>{level.label}</strong></p></article>
        <article><Icon name="calendar" /><p><span>अंतिम तारीख</span><strong>{formatDate(scheme.end_date)}</strong></p></article>
        <article><Icon name="file" /><p><span>कागदपत्रे</span><strong>{formatMarathiNumber(docs.length || 0)}</strong></p></article>
      </section>

      <div className="scheme-detail-layout">
        <aside className="scheme-detail-menu">
          <h2>योजना माहिती</h2>
          {[
            ['overview', 'grid', 'योजनेचा आढावा'],
            ['benefits', 'coins', 'लाभ तपशील'],
            ['eligibility', 'check', 'पात्रता निकष'],
            ['documents', 'file', 'आवश्यक कागदपत्रे'],
            ['dates', 'calendar', 'महत्त्वाच्या तारखा'],
            ['process', 'arrow', 'अर्जाचे टप्पे'],
            ['application', 'arrow', 'अर्ज प्रक्रिया'],
          ].map(([id, icon, label]) => <a key={id} href={`#${id}`}><Icon name={icon} size={17} />{label}</a>)}
        </aside>

        <main className="scheme-detail-main">
          <DetailSection id="overview" icon={category.icon} title="योजनेचा आढावा">
            <p className="scheme-detail-lead">{scheme.description || 'या योजनेचा आढावा उपलब्ध नाही.'}</p>
            <div className="scheme-info-grid">
              <div><span>लघु नाव</span><strong>{scheme.short_name || '-'}</strong></div>
              <div><span>विभाग / प्रकार</span><strong>{category.label}</strong></div>
              <div><span>योजना स्तर</span><strong>{level.label}</strong></div>
              <div><span>स्थिती</span><strong>{scheme.is_active ? 'सक्रिय' : 'निष्क्रिय'}</strong></div>
              {scheme.official_department && <div><span>अंमलबजावणी विभाग</span><strong>{scheme.official_department}</strong></div>}
              {scheme.last_verified_at && <div><span>माहिती पडताळणी</span><strong>{formatDate(scheme.last_verified_at)}</strong></div>}
              {portalUrl && <div><span>ऑनलाइन लिंक / वेबसाइट</span><strong><a href={portalUrl} target="_blank" rel="noreferrer" onClick={event => openExternalWithNotice(event, portalUrl)}>लिंक उघडा</a></strong></div>}
            </div>
            <div className={`scheme-channel-card ${applicationChannel}`}>
              <Icon name={applicationChannel === 'external' ? 'external' : applicationChannel === 'direct' ? 'check' : 'file'} />
              <div>
                <span>अर्ज सुविधा</span>
                <strong>
                  {canApplyOnGp
                    ? 'या ग्रामपंचायत पोर्टलवरून थेट अर्ज करता येईल'
                    : portalUrl
                      ? 'अर्ज अधिकृत बाह्य पोर्टलवरून करावा'
                      : 'अर्ज प्रक्रिया संबंधित कार्यालय/विभागातून होते'}
                </strong>
                <p>
                  {canApplyOnGp
                    ? 'नागरिक खात्यात लॉगिन करून खालील अर्ज फॉर्म सबमिट करा.'
                    : portalUrl
                      ? 'योजना माहिती येथे पहा आणि अर्जासाठी अधिकृत लिंक वापरा.'
                      : 'ही योजना माहिती व मार्गदर्शनासाठी दाखवली आहे; अर्जासाठी ग्रामपंचायत कार्यालयाशी संपर्क साधा.'}
                </p>
              </div>
            </div>
          </DetailSection>

          <DetailSection id="benefits" icon="coins" title="लाभ तपशील">
            <div className="scheme-benefit-box">
              <strong>{toMarathiDigits(scheme.benefit_amount || 'लाभ रक्कम उपलब्ध नाही')}</strong>
              <p>{toMarathiDigits(scheme.benefit_details || 'लाभाचा तपशील अद्याप नोंदवलेला नाही.')}</p>
            </div>
          </DetailSection>

          <DetailSection id="eligibility" icon="check" title="पात्रता निकष">
            {eligibility.length ? (
              <ul className="scheme-detail-list">{eligibility.map((item, index) => <li key={index}><Icon name="check" size={17} />{itemText(item)}</li>)}</ul>
            ) : (
              <p className="scheme-detail-empty">पात्रता निकष उपलब्ध नाहीत.</p>
            )}
          </DetailSection>

          <DetailSection id="documents" icon="file" title="आवश्यक कागदपत्रे">
            {docs.length ? (
              <ul className="scheme-doc-grid">
                {docs.map((doc, index) => (
                  <li key={index}>
                    <Icon name="file" size={17} />
                    {itemText(doc)}
                    <small>{doc.is_required === false ? 'ऐच्छिक' : 'आवश्यक'}</small>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="scheme-detail-empty">कागदपत्रांची यादी उपलब्ध नाही.</p>
            )}
          </DetailSection>

          <DetailSection id="dates" icon="calendar" title="महत्त्वाच्या तारखा">
            <div className="scheme-date-grid">
              <div><span>सुरुवात तारीख</span><strong>{formatDate(scheme.start_date)}</strong></div>
              <div><span>अंतिम तारीख</span><strong>{formatDate(scheme.end_date)}</strong></div>
            </div>
          </DetailSection>

          <DetailSection id="process" icon="arrow" title="अर्ज करण्याची पद्धत">
            {applicationSteps.length ? (
              <ol className="scheme-detail-list scheme-process-list">{applicationSteps.map((item, index) => <li key={index}><span>{toMarathiDigits(index + 1)}</span>{itemText(item)}</li>)}</ol>
            ) : (
              <p className="scheme-detail-empty">अर्जाचे टप्पे उपलब्ध नाहीत. अधिकृत पोर्टल किंवा संबंधित कार्यालयातून माहिती घ्या.</p>
            )}
          </DetailSection>

          <section id="application" className={`scheme-apply-banner ${applicationChannel}`}>
            <Icon name={applicationChannel === 'external' ? 'external' : category.icon} size={44} />
            <div>
              <h2>
                {canApplyOnGp
                  ? 'या योजनेसाठी GP पोर्टलवर अर्ज करा'
                  : portalUrl
                    ? 'अर्ज अधिकृत पोर्टलवर करावा'
                    : 'अर्ज प्रक्रिया कार्यालयातून पूर्ण करा'}
              </h2>
              <p>
                {canApplyOnGp
                  ? 'ही स्थानिक ग्रामपंचायत योजना आहे. नागरिक खात्यातून अर्ज सबमिट करून त्याची स्थिती ट्रॅक करता येईल.'
                  : portalUrl
                    ? 'ही योजना बाह्य सरकारी पोर्टलवर चालते. येथे माहिती तपासा आणि अर्जासाठी अधिकृत वेबसाइट वापरा.'
                    : 'या योजनेसाठी थेट ऑनलाइन अर्ज उपलब्ध नाही. पात्रता, कागदपत्रे आणि प्रक्रिया तपासून कार्यालयाशी संपर्क साधा.'}
              </p>
            </div>
            {canApplyOnGp ? (
              !isAuthenticated ? (
                <Link to={loginTarget}>लॉगिन करून अर्ज करा <Icon name="arrow" size={16} /></Link>
              ) : isCitizen ? (
                <button
                  type="button"
                  onClick={() => {
                    setShowApplyForm(value => !value);
                    setApplyMessage(null);
                  }}
                  aria-expanded={showApplyForm}
                >
                  {showApplyForm ? 'फॉर्म बंद करा' : 'अर्ज फॉर्म उघडा'} <Icon name="arrow" size={16} />
                </button>
              ) : (
                <span className="scheme-apply-note">अर्जासाठी नागरिक खाते आवश्यक आहे.</span>
              )
            ) : portalUrl ? (
              <a href={portalUrl} target="_blank" rel="noreferrer" onClick={event => openExternalWithNotice(event, portalUrl)}>ऑनलाइन लिंक / वेबसाइट <Icon name="external" size={16} /></a>
            ) : (
              <Link to={`/gp/${tenantSlug}/contact`}>संपर्क साधा <Icon name="arrow" size={16} /></Link>
            )}
          </section>

          {applyMessage && (
            <div className={`scheme-apply-message ${applyMessage.type}`}>
              <strong>{applyMessage.text}</strong>
              {applyMessage.reference && <span>संदर्भ क्रमांक: {applyMessage.reference}</span>}
              {applyMessage.type === 'success' && (
                <Link to={`/gp/${tenantSlug}/dashboard`}>माझ्या खात्यात अर्ज स्थिती पहा</Link>
              )}
            </div>
          )}

          {showApplyForm && canApplyOnGp && isCitizen && (
            <form className="scheme-apply-panel" onSubmit={submitSchemeApplication}>
              <header>
                <h2>योजना अर्ज पुष्टी</h2>
                <p>कृपया पात्रता आणि कागदपत्रांची पुष्टी करा. प्रत्यक्ष पडताळणी ग्रामपंचायत कार्यालयाकडून केली जाईल.</p>
              </header>

              {eligibility.length > 0 && (
                <section>
                  <h3>पात्रता पुष्टी</h3>
                  <div className="scheme-apply-checklist">
                    {eligibility.map((item, index) => (
                      <label key={index}>
                        <input
                          type="checkbox"
                          checked={Boolean(applyData.eligibility[index])}
                          onChange={event => setEligibilityConfirmation(index, event.target.checked)}
                        />
                        <span>{itemText(item)}</span>
                      </label>
                    ))}
                  </div>
                </section>
              )}

              {docs.length > 0 && (
                <section>
                  <h3>कागदपत्रे उपलब्ध आहेत?</h3>
                  <div className="scheme-apply-checklist">
                    {docs.map((doc, index) => (
                      <label key={index}>
                        <input
                          type="checkbox"
                          checked={Boolean(applyData.documents[index])}
                          onChange={event => setDocumentConfirmation(index, event.target.checked)}
                        />
                        <span>
                          {itemText(doc)}
                          <small>{doc.is_required === false ? 'ऐच्छिक' : 'आवश्यक'}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                </section>
              )}

              <label className="scheme-apply-notes">
                अतिरिक्त माहिती
                <textarea
                  value={applyData.notes}
                  onChange={event => setApplyData(prev => ({ ...prev, notes: event.target.value }))}
                  placeholder="जर काही विशेष माहिती असेल तर येथे लिहा..."
                  rows={4}
                />
              </label>

              <div className="scheme-apply-actions">
                <button type="button" onClick={() => setShowApplyForm(false)} disabled={submitting}>रद्द करा</button>
                <button type="submit" disabled={submitting}>{submitting ? 'सबमिट होत आहे...' : 'अर्ज सबमिट करा'}</button>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
}
